import React, { useEffect } from 'react';
import { PaymentElement, useElements, useStripe } from '@stripe/react-stripe-js';
import { BACKEND_URL, FRONTEND_CNET_URL, RG } from '../../../utils/constants';
import { PAYMENT_MODE } from '../../../utils/payments';
import fetch from '../../../utils/fetch';
import PropTypes from 'prop-types';

const debug = require('debug')('cw:RevolutStripeForm');

export const cancelPaymentOnDrupal = (orderId, paymentId) => {
  fetch(`${BACKEND_URL}/commerce/payment/void/${orderId}/${paymentId}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
  });
};

// Helper function to complete payment after the order has been created.
export async function completePayment(
  stripe,
  elements,
  orderId,
  donationType,
  checkoutUrl,
  giftaid,
  amount,
) {
  try {
    // Stripe says elements.submit() should be called as soon
    // as pay button is pressed, before any async work.
    elements.submit();

    // Create payment and receive client secret.
    const responseCreate = await fetch(`${BACKEND_URL}/commerce/payment/create/${orderId}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: {
        gateway: `decoupled_stripe_revolut_${donationType === RG ? 'rg_' : ''}${PAYMENT_MODE}`,
        type: 'credit_card',
      },
    });

    const paymentId = responseCreate.body.payment_id[0].value;
    const clientSecret = responseCreate.body.remote_id[0].value;

    // TODO: Passing order_id and payment_id is simplest solution
    //   but the proper one would be resolve it from payment intent ID and
    //   client secret, which are returned from Stripe.
    // Amount passed for setting it on checkout form in case of payment error.
    const returnUrl = checkoutUrl.includes('?')
      ? `${FRONTEND_CNET_URL}${checkoutUrl}&amount=${amount}&order_id=${orderId}&payment_id=${paymentId}&giftaid=${giftaid}`
      : `${FRONTEND_CNET_URL}${checkoutUrl}?amount=${amount}&order_id=${orderId}&payment_id=${paymentId}&giftaid=${giftaid}`;

    const { error } =
      donationType === RG
        ? await stripe.confirmSetup({
            //`Elements` instance that was used to create the Setup Element
            elements,
            clientSecret,
            confirmParams: {
              return_url: returnUrl,
            },
          })
        : await stripe.confirmPayment({
            //`Elements` instance that was used to create the Payment Element
            elements,
            clientSecret,
            confirmParams: {
              return_url: returnUrl,
            },
          });

    if (error) {
      debug('Stripe.js could not handle payment/setup.', error);
      cancelPaymentOnDrupal(orderId, paymentId);
      return {
        type: 'error',
        errorMessage:
          'There has been an issue with your payment. Please re-check your payment details or check with your bank.',
      };
    }

    // In case of no errors the customer will be redirected to 'return_url'.
    // So nothing else can be executed here.
    return {
      type: 'success',
      orderId: orderId,
    };
  } catch (error) {
    debug('Could not process Stripe payment.', error);
    // Show message to the user.
    return {
      type: 'error',
      errorMessage: '',
    };
  }
}

export async function completeOrder(orderId, paymentId) {
  // We call this function only when return_url received successful result
  // from Stripe. So we redirect user to the thank you page in any case.
  try {
    // Inform the backend about successful payment and finalize donation on Drupal.
    await fetch(`${BACKEND_URL}/commerce/payment/capture/${orderId}/${paymentId}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
    });
  } catch (error) {
    debug('Could not process Stripe payment.', error);
  }

  return {
    type: 'success',
    orderId: orderId,
  };
}

export const hasReturnUrlValuesInQuery = (query) =>
  query &&
  query.order_id &&
  query.payment_id &&
  query.redirect_status &&
  ((query.type === 'single' && query.payment_intent && query.payment_intent_client_secret) ||
    (query.type === 'monthly' && query.setup_intent && query.setup_intent_client_secret));

const RevolutStripeForm = ({ onCompletePayment, orderId = null }) => {
  const stripe = useStripe();
  const elements = useElements();

  useEffect(() => {
    if (!orderId) {
      return;
    }
    if (onCompletePayment) {
      onCompletePayment(stripe, elements, orderId);
    }
  }, [orderId, stripe]);

  if (!stripe) {
    return <div>Loading...</div>;
  }

  return (
    <>
      <PaymentElement />
    </>
  );
};

RevolutStripeForm.propTypes = {
  orderId: PropTypes.number,
  onCompletePayment: PropTypes.func.isRequired,
};

export default RevolutStripeForm;
